/**
 * Auth Form Validation
 * Client-side validation for login, registration, and comment forms
 * 
 * @since 3.7.0
 */

(function($) {
    'use strict';

    /**
     * Validate email format
     */
    function isValidEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    }

    /**
     * Validate username format
     */
    function isValidUsername(username) {
        // Alphanumeric and underscores, 3-20 characters
        const re = /^[a-zA-Z0-9_]{3,20}$/;
        return re.test(username);
    }

    /**
     * Show field error
     */
    function showFieldError($field, message) {
        // Remove existing error
        $field.siblings('.field-error').remove();
        $field.removeClass('field-valid').addClass('field-error-state');

        // Add error message
        $field.after(`<span class="field-error">${message}</span>`);
    }

    /**
     * Clear field error
     */
    function clearFieldError($field) {
        $field.siblings('.field-error').remove();
        $field.removeClass('field-error-state');
    }

    /**
     * Show field success
     */
    function showFieldSuccess($field) {
        clearFieldError($field);
        $field.addClass('field-valid');
    }

    /**
     * Validate login form
     */
    function validateLoginForm($form) {
        let isValid = true;

        // Username/Email
        const $username = $form.find('input[name="username"]');
        const username = $username.val().trim();

        if (username.length === 0) {
            showFieldError($username, 'Please enter your username or email.');
            isValid = false;
        } else {
            clearFieldError($username);
        }

        // Password
        const $password = $form.find('input[name="password"]');
        const password = $password.val();

        if (password.length === 0) {
            showFieldError($password, 'Please enter your password.');
            isValid = false;
        } else {
            clearFieldError($password);
        }

        return isValid;
    }

    /**
     * Validate registration form
     */
    function validateRegistrationForm($form) {
        let isValid = true;

        // Username
        const $username = $form.find('input[name="username"]');
        const username = $username.val().trim();

        if (username.length === 0) {
            showFieldError($username, 'Please enter a username.');
            isValid = false;
        } else if (!isValidUsername(username)) {
            showFieldError($username, 'Username must be 3-20 characters (letters, numbers, underscores only).');
            isValid = false;
        } else if ($username.hasClass('taken')) {
            showFieldError($username, 'This username is already taken.');
            isValid = false;
        } else {
            showFieldSuccess($username);
        }

        // Email
        const $email = $form.find('input[name="email"]');
        const email = $email.val().trim();

        if (email.length === 0) {
            showFieldError($email, 'Please enter your email address.');
            isValid = false;
        } else if (!isValidEmail(email)) {
            showFieldError($email, 'Please enter a valid email address.');
            isValid = false;
        } else {
            showFieldSuccess($email);
        }

        // Password
        const $password = $form.find('input[name="password"]');
        const password = $password.val();

        if (password.length === 0) {
            showFieldError($password, 'Please enter a password.');
            isValid = false;
        } else if (password.length < 8) {
            showFieldError($password, 'Password must be at least 8 characters long.');
            isValid = false;
        } else if ($password.hasClass('password-weak')) {
            showFieldError($password, 'Please use a stronger password.');
            isValid = false;
        } else {
            showFieldSuccess($password);
        }

        // Confirm Password
        const $confirmPassword = $form.find('input[name="confirm_password"]');
        const confirmPassword = $confirmPassword.val();

        if (confirmPassword.length === 0) {
            showFieldError($confirmPassword, 'Please confirm your password.');
            isValid = false;
        } else if (password !== confirmPassword) {
            showFieldError($confirmPassword, 'Passwords do not match.');
            isValid = false;
        } else {
            showFieldSuccess($confirmPassword);
        }

        // Terms checkbox (if exists and required)
        const $terms = $form.find('input[name="terms"]');
        if ($terms.length > 0 && $terms.prop('required')) {
            if (!$terms.is(':checked')) {
                showFieldError($terms.closest('.text-fields'), 'You must accept the terms and conditions.');
                isValid = false;
            } else {
                clearFieldError($terms.closest('.text-fields'));
            }
        }

        return isValid;
    }

    /**
     * Validate comment form
     */
    function validateCommentForm($form) {
        let isValid = true;

        // Author name
        const $author = $form.find('input[name="author"]');
        const author = $author.val().trim();

        if (author.length === 0) {
            showFieldError($author, 'Please enter your name.');
            isValid = false;
        } else {
            showFieldSuccess($author);
        }

        // Email
        const $email = $form.find('input[name="email"]');
        const email = $email.val().trim();

        if (email.length === 0) {
            showFieldError($email, 'Please enter your email address.');
            isValid = false;
        } else if (!isValidEmail(email)) {
            showFieldError($email, 'Please enter a valid email address.');
            isValid = false;
        } else {
            showFieldSuccess($email);
        }

        // Comment
        const $comment = $form.find('textarea[name="comment"]');
        const comment = $comment.val().trim();

        if (comment.length === 0) {
            showFieldError($comment, 'Please enter your comment.');
            isValid = false;
        } else if (comment.length < 10) {
            showFieldError($comment, 'Comment must be at least 10 characters long.');
            isValid = false;
        } else {
            showFieldSuccess($comment);
        }

        return isValid;
    }

    /**
     * Detect form type
     */
    function detectFormType($form) {
        // Check for specific field combinations
        if ($form.find('input[name="username"]').length > 0 && 
            $form.find('input[name="confirm_password"]').length > 0) {
            return 'registration';
        } else if ($form.find('input[name="username"]').length > 0 && 
                   $form.find('input[name="password"]').length > 0) {
            return 'login';
        } else if ($form.find('input[name="author"]').length > 0 && 
                   $form.find('textarea[name="comment"]').length > 0) {
            return 'comment';
        }
        return 'unknown';
    }

    /**
     * Initialize form validation
     */
    function initFormValidation() {
        // Find all Simple Form forms
        $('.simple_form').each(function() {
            const $form = $(this);
            const formType = detectFormType($form);

            if (formType === 'unknown') return;

            // Validate on submit
            $form.on('submit', function(e) {
                let isValid = false;

                switch (formType) {
                    case 'login':
                        isValid = validateLoginForm($form);
                        break;
                    case 'registration':
                        isValid = validateRegistrationForm($form);
                        break;
                    case 'comment':
                        isValid = validateCommentForm($form);
                        break;
                }

                if (!isValid) {
                    e.preventDefault();
                    
                    // Scroll to first error
                    const $firstError = $form.find('.field-error-state').first();
                    if ($firstError.length > 0) {
                        $('html, body').animate({
                            scrollTop: $firstError.offset().top - 100
                        }, 300);
                        $firstError.focus();
                    }
                }
            });

            // Real-time validation on blur
            $form.find('input, textarea').on('blur', function() {
                const $field = $(this);
                const fieldName = $field.attr('name');

                // Skip if field is empty (will be caught on submit)
                if ($field.val().trim().length === 0) return;

                // Validate specific fields
                if (fieldName === 'email') {
                    if (!isValidEmail($field.val().trim())) {
                        showFieldError($field, 'Please enter a valid email address.');
                    } else {
                        showFieldSuccess($field);
                    }
                } else if (fieldName === 'username' && formType === 'registration') {
                    if (!isValidUsername($field.val().trim())) {
                        showFieldError($field, 'Username must be 3-20 characters (letters, numbers, underscores only).');
                    } else if (!$field.hasClass('taken')) {
                        showFieldSuccess($field);
                    }
                }
            });
        });
    }

    // Initialize on document ready
    $(document).ready(function() {
        initFormValidation();
    });

    // Re-initialize when new forms are loaded dynamically
    $(document).on('simpleform_loaded', function() {
        initFormValidation();
    });

})(jQuery);
